/**
 * Season-model 2c — `useStartSeason` orchestrator.
 *
 * The "Start your season" CTA chain, in ONE place (consumed by both
 * `/v2/plan` and `/v2/today` no-season empty-states):
 *
 *   1. `useCreateSeason.create()` with provisional dates (today → +16 weeks)
 *      and an auto-name. Returns the new season's EXPLICIT id.
 *   2. `useGenerateMacroPlan.generate({ seasonId })` with THAT id — never a
 *      refetched/stale `useActiveSeason` cache. 2a's reconciliation then
 *      overwrites the provisional dates with the real macro span.
 *   3. `onComplete()` so the caller refetches active-season + macro and the
 *      UI flips from "no season" to the generated plan.
 *
 * Duplicate-submit protection: a single combined `starting` flag (true from
 * the click until create+generate settle) drives the CTA's disabled state,
 * and an in-flight ref makes re-entry a no-op. The backend
 * (`handleCreateSeason`) also guards against a second active season.
 *
 * Per the locked decision, NO goal race is required — generate runs with
 * whatever meets exist (16-week fallback when none / no priority<=2 meet).
 */

import { useCallback, useRef, useState } from 'react';
import { useCreateSeason } from './useCreateSeason';
import { useGenerateMacroPlan } from './useGenerateMacroPlan';
import { addWeeksIso, localTodayIso, mondayOnOrBeforeIso } from '../lib/macroPlanPhases';
// Round-29 critic 7 — the sentence this hook RAISES lives in the copy module
// with the season surface's other strings of record, not here: a component
// that imports a constant from a hooks module is broken by any test that mocks
// the module without spreading `...actual`. Imported, never restated.
import { SEASON_NOT_ROLLED_OVER_MESSAGE } from '../lib/seasonPlanCopy';

/** Provisional horizon before 2a reconciles to the real anchor-derived span. */
const DEFAULT_HORIZON_WEEKS = 16;

export interface UseStartSeasonArgs {
  /** Athlete's current team, associated with the new season when present. */
  teamId?: string | null;
  /** Season name (e.g. derived from a goal race); else "Training Season {year}". */
  name?: string | null;
  /** Refetch active-season + macro after a successful start so the UI updates. */
  onComplete?: () => Promise<void> | void;
}

export type StartSeasonPhase = 'idle' | 'creating' | 'generating';

export interface StartSeasonOptions {
  /**
   * SR-2 roll-forward guard: when true, a `deduped` create (the server
   * returned an EXISTING active season instead of inserting) ABORTS the
   * chain with roll-forward-specific copy. B13 (2026-08-21): a dedup now
   * aborts in EVERY flow — "a dedup is the benign double-submit case" was
   * wrong: a deduped season can be a live mid-flight season a stale
   * no-season render never knew about, and generating into it re-anchors
   * (wipes) the athlete's season view. This flag only picks the message.
   */
  expectNew?: boolean;
}

export interface UseStartSeasonResult {
  /** Run create→generate. Never throws; failures surface via `error`. */
  start: (opts?: StartSeasonOptions) => Promise<{ ok: boolean; error?: Error }>;
  /** True from click until create+generate settle — drives the CTA disable. */
  starting: boolean;
  /** Granular phase for copy ("Creating season…" / "Generating plan…"). */
  phase: StartSeasonPhase;
  /** Latest failure (create or generate). Null while idle/ok. */
  error: Error | null;
}

export function useStartSeason({
  teamId,
  name,
  onComplete,
}: UseStartSeasonArgs = {}): UseStartSeasonResult {
  const { create } = useCreateSeason();
  // Bound seasonId is null — we always pass the freshly-created id explicitly.
  const { generate } = useGenerateMacroPlan({ seasonId: null });
  const inFlightRef = useRef<boolean>(false);
  const [phase, setPhase] = useState<StartSeasonPhase>('idle');
  const [error, setError] = useState<Error | null>(null);

  const start = useCallback(async (opts?: StartSeasonOptions) => {
    if (inFlightRef.current) {
      return { ok: false as const, error: new Error('Already starting a season') };
    }
    inFlightRef.current = true;
    setError(null);
    setPhase('creating');
    try {
      // SP-1 — snap the provisional start to the creation week's Monday, matching
      // the macro generator's anchor (2a reconciliation overwrites these with the
      // real span; this keeps the pre-reconcile UI on the same week model).
      const startDate = mondayOnOrBeforeIso(localTodayIso());
      const endDate = addWeeksIso(startDate, DEFAULT_HORIZON_WEEKS);
      const seasonName =
        name && name.trim() ? name.trim() : `Training Season ${new Date().getFullYear()}`;

      const created = await create({
        name: seasonName,
        startDate,
        endDate,
        sport: 'cross-country',
        teamId: teamId ?? null,
      });
      if (!created.ok || !created.season) {
        const err = created.error ?? new Error('Failed to create season');
        setError(err);
        return { ok: false as const, error: err };
      }

      // SR-2 wipe-guard, widened by B13 (2026-08-21): a deduped create means
      // the server returned a PRE-EXISTING active season. Generating into it
      // re-anchors a season the athlete may be mid-way through — the race-day
      // morning wipe class. The CTA that fired this chain believed there was
      // NO season (a stale or failed read), so the honest outcome is a calm
      // error + reload, never a re-plan. EVERY flow now aborts on dedup; the
      // roll-forward keeps its specific copy.
      if (created.deduped) {
        const err = new Error(
          opts?.expectNew
            ? SEASON_NOT_ROLLED_OVER_MESSAGE
            // B124 (register: no em or en dashes) — a period, not a dash.
            : 'You already have an active season. Reload to see it.',
        );
        setError(err);
        return { ok: false as const, error: err };
      }

      setPhase('generating');
      const gen = await generate({ seasonId: created.season.id });
      // Always refetch — the season now exists either way. On generate failure
      // the season is status 'none', so the UI lands on the "Generate" CTA.
      await onComplete?.();
      if (!gen.ok) {
        const err = gen.error ?? new Error('Failed to generate plan');
        setError(err);
        return { ok: false as const, error: err };
      }
      return { ok: true as const };
    } finally {
      inFlightRef.current = false;
      setPhase('idle');
    }
  }, [create, generate, name, teamId, onComplete]);

  return { start, starting: phase !== 'idle', phase, error };
}
